import { detectPlatform } from "../lib/Platform";

// 앱 셸 스플래시(#app-splash 오버레이)의 종료를 제어한다. 오버레이는 index.html
// 파싱 시점에 그려지고(vite.config.ts injectSplash), main.tsx 가 createRoot 첫 페인트
// 직후 finishAppSplash() 를 호출한다.
//
// 두 가지를 보장한다:
//  1. 최소 노출 시간 — 앱이 아주 빨리 마운트돼도 스플래시가 깜빡이지 않게.
//  2. 네이티브 순서 — 웹 스플래시가 사라진 "뒤에" 네이티브 스플래시를 내린다.
//     (반대로 하면 네이티브가 먼저 꺼져 그 밑의 웹 스플래시가 노출된다.)
//     주의: Android 12+ 에선 Capacitor 가 hide() 전까지 웹뷰 그리기 자체를 막으므로 웹
//     스플래시는 안드로이드에서 노출되지 않는다 — 네이티브 부팅 연출은 시스템 스플래시
//     아이콘 AVD(scripts/write-android-splash.mjs)가 담당한다.

const MIN_VISIBLE_MS = 500;
const FADE_MS = 200; // splash.css 의 transition 시간과 일치시킬 것.
// 스플래시는 이제 "실제 화면 마운트"(SplashDismissOnMount)가 걷는다. 다만 세션 판정이
// 영영 안 끝나는 극단(오프라인/캡티브 포털 등, App 이 계속 null 을 반환)에서는 마커가
// 마운트되지 않아 로고가 얼어붙을 수 있으니, 이 시간이 지나면 강제로 걷는 안전망을 둔다.
const MAX_VISIBLE_MS = 8000;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// 웹 스플래시가 제거된 뒤에만 호출된다. 네이티브(android/ios)에서만 동작하고,
// @capacitor/splash-screen 은 동적 import 로 웹 entry 청크에서 뺀다(기존 네이티브
// 플러그인 lazy 로딩 컨벤션과 동일). 네이티브 스플래시를 제어하려면 네이티브 config
// 에 launchAutoHide:false 가 있어야 한다(scripts/write-capacitor-config.mjs).
async function hideNativeSplash(): Promise<void> {
  const platform = detectPlatform();
  if (platform !== "android" && platform !== "ios") return;
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: FADE_MS });
  } catch {
    // 플러그인이 네이티브 프로젝트에 아직 sync 안 됐거나 브리지 부재 — 조용히 무시.
  }
}

let finished = false;

/** 첫 페인트 직후 1회 호출. 최소 노출 시간 뒤 웹 스플래시를 페이드아웃·제거하고,
 *  네이티브에서는 그 뒤에 네이티브 스플래시를 내린다. */
export async function finishAppSplash(): Promise<void> {
  if (finished) return;
  finished = true;

  // 최소 노출 보장. performance.now() 는 네비게이션 시작 후 경과(ms)이며, 스플래시는
  // HTML 파싱 직후 그려지므로 "스플래시가 뜬 뒤 경과 시간"의 충분한 근사치다.
  const remaining = MIN_VISIBLE_MS - performance.now();
  if (remaining > 0) await wait(remaining);

  const el = document.getElementById("app-splash");
  if (el) {
    el.classList.add("app-splash-hiding");
    await wait(FADE_MS);
    el.remove();
  }

  await hideNativeSplash();
}

/** 부팅 시 1회 호출. 실제 화면이 끝내 마운트되지 않아도 MAX_VISIBLE_MS 뒤에는
 *  스플래시를 강제로 걷어 로고가 무한정 얼어붙지 않게 한다. 정상 경로에서는 화면
 *  마운트가 먼저 finishAppSplash() 를 호출하므로 이 타임아웃은 finished 가드로 no-op. */
export function scheduleSplashDeadline(): void {
  setTimeout(() => void finishAppSplash(), MAX_VISIBLE_MS);
}
